import React from 'react';
import {
  prospectDetailSectionButtonId,
  prospectDetailSectionPanelId,
} from './adminProspectDetailMobileSections.js';

/**
 * Mobile disclosure shell for Prospect 360 major sections.
 *
 * IMPORTANT: the ancestor tree around `children` is identical on desktop and
 * mobile so stateful descendants are not remounted when crossing 768px.
 * Presentation (disclosure chrome / visibility) changes via props/CSS only.
 */
export default function AdminProspectDetailMobileSection({
  sectionId,
  title,
  summary = '',
  isMobile = false,
  open = true,
  onToggle,
  children,
  headingLevel = 2,
}) {
  const panelId = prospectDetailSectionPanelId(sectionId);
  const buttonId = prospectDetailSectionButtonId(sectionId);
  const HeadingTag = headingLevel === 3 ? 'h3' : 'h2';
  const expanded = Boolean(open);
  const showDisclosure = Boolean(isMobile);
  const panelHidden = showDisclosure && !expanded;

  return React.createElement(
    'section',
    {
      className: showDisclosure
        ? 'rounded-xl border border-slate-200 bg-white p-4'
        : 'min-w-0',
      'data-prospect-section': sectionId,
    },
    React.createElement(
      HeadingTag,
      {
        className: showDisclosure ? 'm-0' : 'sr-only',
        'data-prospect-section-heading': sectionId,
      },
      React.createElement(
        'button',
        {
          id: buttonId,
          type: 'button',
          'aria-expanded': showDisclosure ? expanded : true,
          'aria-controls': panelId,
          tabIndex: showDisclosure ? 0 : -1,
          onClick: () => {
            if (!showDisclosure) return;
            onToggle?.();
          },
          className: showDisclosure
            ? 'flex w-full min-h-11 items-start justify-between gap-3 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-indigo-500'
            : 'sr-only',
        },
        React.createElement(
          'span',
          { className: 'min-w-0' },
          React.createElement(
            'span',
            { className: 'block text-sm font-semibold text-slate-800' },
            title,
          ),
          summary
            ? React.createElement(
                'span',
                { className: 'mt-0.5 block text-xs text-slate-500' },
                summary,
              )
            : null,
        ),
        showDisclosure
          ? React.createElement(
              'span',
              {
                className: 'mt-0.5 shrink-0 text-lg leading-none text-slate-500',
                'aria-hidden': 'true',
              },
              expanded ? '▾' : '▸',
            )
          : null,
      ),
    ),
    React.createElement(
      'div',
      {
        id: panelId,
        role: 'region',
        'aria-labelledby': buttonId,
        className: panelHidden ? 'hidden' : showDisclosure ? 'mt-3 space-y-4' : 'min-w-0',
        hidden: panelHidden,
        'data-prospect-section-panel': sectionId,
      },
      children,
    ),
  );
}
